import React, { useState, useEffect, useContext } from "react";
import client from "../../api/client";
import { AuthContext } from "../../contextStore/AuthContext";
import "./PlansModal.css";


export default function SellerPlansModal({
  open,
  onClose,
  initialPlans = null,
  listingId = null,
  onPurchased = () => { },
}) {
  const { token, user } = useContext(AuthContext);
  const [plans, setPlans] = useState(initialPlans || []);
  const [loading, setLoading] = useState(false);
  const [buyingPlanId, setBuyingPlanId] = useState(null);
  const [error, setError] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  const [showPaymentChoiceFor, setShowPaymentChoiceFor] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFetchError(null);

    const fetchPlans = async () => {
      setLoading(true);
      try {
        const url = `/api/plans?type=seller`;
        const res = await client.get(url);
        if (res?.data?.plans) {
          setPlans(res.data.plans.map(normalizePlan));
        } else setFetchError("No seller plans found.");
      } catch (e) {
        console.error("Error loading plans:", e);
        setFetchError("Failed to load seller plans.");
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [open]);

  function centsToDisplay(cents) {
    return (Number(cents || 0) / 100).toFixed(2);
  }

  function normalizePlan(p) {
    const desc =
      p.description ||
      p.desc ||
      p.admin_description ||
      p.description_items ||
      "";

    let description_list = [];
    if (Array.isArray(desc)) description_list = desc;
    else if (typeof desc === "string") {
      description_list = desc
        .split(/\r?\n|<br\s*\/?>|•|-/)
        .map((s) => s.trim())
        .filter(Boolean);
    }

    const mrp_cents = p.mrp_cents || p.mrp_price * 100 || p.price_cents || 0;
    const discounted_cents = p.discounted_price_cents || p.price_cents || 0;
    const percent_off =
      mrp_cents > discounted_cents
        ? Math.round(((mrp_cents - discounted_cents) / mrp_cents) * 100)
        : null;

    return {
      ...p,
      currency: p.currency || "INR",
      mrp_display: mrp_cents ? centsToDisplay(mrp_cents) : "",
      discounted_display: discounted_cents
        ? centsToDisplay(discounted_cents)
        : "",
      price_display: centsToDisplay(discounted_cents || mrp_cents),
      _percent_off: percent_off,
      description_list,
    };
  }

  async function getCurrentUserId() {
    if (user?.id) return user.id;
    try {
      const res = await client.get("/api/auth/me", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      return res?.data?.user?.id || null;
    } catch {
      return null;
    }
  }

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) return resolve();
      const s = document.createElement("script");
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.body.appendChild(s);
    });
  }

  // ---------- Mock Flow ----------
  async function runMockFlow(createOrderResponse, plan) {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const resPayment = createOrderResponse?.data?.payment;
    if (!resPayment) throw new Error("create-order missing payment response");

    const mockBody = {
      payment_id: resPayment.id,
      provider_payment_id: `mock_${Date.now()}`,
      type: "plan",
      user_id: resPayment.user_id || (await getCurrentUserId()),
      plan_id: plan.id,
      amount: Number(plan.price_display || 0),
      currency: plan.currency || "INR",
      meta: { planType: "seller", planId: plan.id },
    };

    const mockRes = await client.post("/api/payments/mock/complete", mockBody, {
      headers,
    });

    if (!mockRes?.data?.ok) throw new Error("Mock payment failed");

    return { activated: true, paymentId: resPayment.id };
  }

  // ---------- Razorpay Flow ----------
  async function runRazorpayFlow(createOrderResponse, plan) {
    const data = createOrderResponse?.data;
    if (!data || !data.ok || !data.razorpay_order)
      throw new Error("Server did not return Razorpay order");

    const razorOrder = data.razorpay_order;
    const keyId = data.razorpay_key_id || process.env.REACT_APP_RAZORPAY_KEY_ID;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const paymentRow = data?.payment || {};

    const paymentId =
      paymentRow.id ||
      data?.payment?.id ||
      data?.paymentId ||
      null;

    if (!paymentId) {
      throw new Error("Payment ID missing from server response");
    }
    const userId = paymentRow.user_id || (await getCurrentUserId());

    if (!userId) {
      throw new Error("User not identified. Please login again.");
    }



    await loadScript("https://checkout.razorpay.com/v1/checkout.js");

    const options = {
      key: keyId,
      amount: razorOrder.amount,
      currency: razorOrder.currency || "INR",
      name: "Old Machine Market",
      description: plan.name || "Seller Plan",
      order_id: razorOrder.id,

      // 🔴 REQUIRED FOR NETBANKING / WALLET / REDIRECT FLOWS
      callback_url: "https://oldmachinemarket.in/api/payments/razorpay/callback",
      redirect: true,

      prefill: {
        name: user?.name || "",
        email: user?.email || "",
      },

      notes: {
        internal_payment_id: paymentId,
        user_id: userId,
        plan_id: plan.id,
        listing_id: listingId,
        planType: "seller",
      }

      ,

      // ✅ OPTIONAL: still works for UPI / inline cards
      handler: async function (response) {
        try {
          const verifyRes = await client.post(
            "/api/payments/razorpay/verify",
            {
              razorpay_order_id: razorOrder.id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              type: "plan",
              user_id: userId,
              plan_id: plan.id,
              listing_id: listingId || null,
              currency: plan.currency || "INR",
              meta: { planType: "seller", planId: plan.id, listing_id: listingId },
            },
            { headers }
          );

          if (verifyRes?.data?.ok) {

            // 🔥 PUBLISH THE MACHINE
            if (listingId && paymentId) {
              await client.post('/api/seller/publish', {
                machineId: listingId,
                paymentId
              });
            }

            onPurchased({
              type: "plan",
              planId: plan.id,
              paymentId,
              listingId,
            });

            setBuyingPlanId(null);
            onClose?.();
          }

        } catch (err) {
          console.warn("Inline verify failed (redirect flow will handle it)", err);
        }
      },

      modal: {
        ondismiss: () => {
          setBuyingPlanId(null);
        },
      },
    };


    const rzp = new window.Razorpay(options);
    rzp.open();
  }

  // ---------- Create Order ----------
  async function createOrderOnServer({ plan, provider = "razorpay" }) {
    const userId = await getCurrentUserId();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // 🔍 ADD DEBUG LOG HERE - inside the function
    console.log('🔍 createOrderOnServer called with:', {
      listingId,
      planId: plan.id,
      planName: plan.name,
      userId,
      userEmail: user?.email
    });

    if (!listingId) {
      console.error('❌ CRITICAL: listingId is null/undefined!');
      throw new Error("Listing ID missing. Please retry posting the machine.");
    }

    // In createOrderOnServer function, ensure meta includes machineId:
    const body = {
      user_id: userId,
      type: "plan",
      plan_id: plan.id,
      listing_id: listingId,
      amount: Number(plan.price_display),
      currency: plan.currency || "INR",
      provider,
      meta: {
        planType: "seller",
        planId: plan.id,
        machineId: listingId,  // 🔥 CRITICAL: This is what sellerService.js looks for
        listing_id: listingId, // For compatibility
      },
    };

    // 🔍 ADD DEBUG LOG HERE - before sending request
    console.log('🔍 Sending payment request with body:', body);

    const res = await client.post("/api/payments/create-order", body, { headers });

    // 🔍 ADD DEBUG LOG HERE - after response
    console.log('🔍 Payment create-order response:', res.data);

    if (!res?.data?.ok) {
      throw new Error("Create order failed");
    }

    return res;
  }


  // ---------- Buy button ----------
  function onBuyClick(plan) {
    setError(null);
    setShowPaymentChoiceFor(plan);
  }

  // ---------- Payment Choice Handler ----------
  async function handlePaymentChoice(choice, plan) {
    setError(null);
    setBuyingPlanId(plan.id);
    setCheckoutLoading(true);
    setShowPaymentChoiceFor(null);

    try {
      // 🟢 FREE LISTING (NO PAYMENT, NO ORDER)
      if (choice === "free" || Number(plan.price_display) === 0) {
        onPurchased({
          type: "plan",
          planId: plan.id,
          free: true,
        });

        setBuyingPlanId(null);
        setCheckoutLoading(false);
        onClose?.();
        return;
      }

      // 🔵 PAID FLOW (RAZORPAY ONLY)
      const createRes = await createOrderOnServer({
        plan,
        provider: "razorpay",
      });

      await runRazorpayFlow(createRes, plan);
      // onPurchased will be called from Razorpay callback

    } catch (err) {
      console.error("handlePaymentChoice error", err);
      setError(err?.message || "Payment failed");
    } finally {
      setBuyingPlanId(null);
      setCheckoutLoading(false);
    }
  }


  if (!open) return null;


  return (
    <div className="plans-modal-backdrop">
      <div className="plans-modal">
        <div className="plans-modal-header">
          <h3>Seller Plans</h3>
          <button className="plans-close-btn" onClick={() => onClose?.()}>
            Close
          </button>
        </div>

        {loading && <div className="plans-loading">Loading plans...</div>}
        {fetchError && <div className="plans-error">{fetchError}</div>}
        {error && <div className="plans-error">{error}</div>}

        <div className="plans-grid">
          {plans.length ? (
            plans.map((p) => {
              const currencySymbol =
                (p.currency || "INR").toUpperCase() === "INR" ? "₹" : p.currency || "";
              return (
                <div key={p.id} className="plans-card">
                  <div className="plans-card-header">
                    <div className="plans-card-left">
                      <h4 className="plans-card-title">{p.name}</h4>
                      {p.description_list && p.description_list.length ? (
                        <ul className="plans-desc-list">
                          {p.description_list.map((line, i) => (
                            <li key={i}>
                              <span className="desc-check">✓</span>
                              <span className="desc-text">{line}</span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>

                    <div className="plans-price-block-vertical price-right">
                      {p._percent_off ? (
                        <div className="percent-off-badge">{p._percent_off}% OFF</div>
                      ) : null}

                      {p.mrp_display && (
                        <div className="mrp-line">
                          <span className="mrp-label">MRP:</span>
                          <span className="mrp-value">{currencySymbol}{p.mrp_display}</span>
                        </div>
                      )}

                      <div className="final-price-large discounted-price">
                        <span className="currency-lg">{currencySymbol}</span>
                        <span className="amount-lg">{p.discounted_display || p.price_display}</span>
                      </div>

                      <div className="tax-note">All prices inclusive of taxes</div>
                    </div>
                  </div>

                  <div className="plans-card-footer">
                    <button
                      className="plans-buy-btn"
                      disabled={buyingPlanId === p.id || checkoutLoading}
                      onClick={() => onBuyClick(p)}
                    >
                      {buyingPlanId === p.id ? "Processing…" : "Buy Plan"}
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            !loading && <div className="plans-no">No seller plans available.</div>
          )}
        </div>

        {showPaymentChoiceFor && (
          <div className="payment-choice-backdrop">
            <div className="payment-choice-modal">
              <h4>Choose Payment Method</h4>
              <p>
                Purchasing <strong>{showPaymentChoiceFor.name}</strong>
              </p>
              <div className="payment-choice-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowPaymentChoiceFor(null)}
                >
                  Cancel
                </button>


                <button
                  className="btn btn-primary"
                  onClick={() =>
                    handlePaymentChoice("razorpay", showPaymentChoiceFor)
                  }
                >
                  Pay with Razorpay
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
